import { markRaw } from 'vue'
import TeamMemberNameColumn from './TeamMemberNameColumn.vue'
import TeamMemberEmailColumn from './TeamMemberEmailColumn.vue'
import TeamMemberPhoneColumn from './TeamMemberPhoneColumn.vue'
import TeamMemberAccountRoleColumn from './TeamMemberAccountRoleColumn.vue'
import {
  CompanyTeamRole,
  ClientRelationTeamOrderByColumn,
  TeamMemberAccountRoleColumnFragment,
  TeamMemberAccountRoleColumnFragmentDoc,
  TeamMemberEmailColumnFragment,
  TeamMemberEmailColumnFragmentDoc,
  TeamMemberNameColumnFragment,
  TeamMemberNameColumnFragmentDoc,
  TeamMemberPhoneColumnFragment,
  TeamMemberPhoneColumnFragmentDoc,
  UserEmailColumnFragment,
  UserNameColumnFragment,
  UserPhoneColumnFragment,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { sortPair } from '@/components/GraphqlDataTable/columns/sortPair'
import { formatPhone } from '@/utils/phone'

// Nested user fragments are masked in the generated row type, so the describers read them as plain objects.
const memberUser = (row: { user: unknown }) =>
  row.user as UserNameColumnFragment & UserEmailColumnFragment & UserPhoneColumnFragment

const columns: ColumnMaps = {
  TeamMemberNameColumn: {
    fragment: TeamMemberNameColumnFragmentDoc,
    component: markRaw(TeamMemberNameColumn),
    columnDef: {
      title: $t('Name'),
      key: 'TeamMemberNameColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(TeamMemberNameColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      { title: 'Name', extraction: (row: TeamMemberNameColumnFragment) => memberUser(row).name },
    ],
    sort: sortPair(ClientRelationTeamOrderByColumn.Name, $t('Name'), $t('A - Z'), $t('Z - A')),
  },
  TeamMemberAccountRoleColumn: {
    fragment: TeamMemberAccountRoleColumnFragmentDoc,
    component: markRaw(TeamMemberAccountRoleColumn),
    columnDef: {
      title: $t('Account role'),
      key: 'TeamMemberAccountRoleColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(TeamMemberAccountRoleColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Account role',
        extraction: (row: TeamMemberAccountRoleColumnFragment) =>
          row.accountRole === CompanyTeamRole.Owner ? 'Owner' : 'Member',
      },
    ],
    sort: sortPair(
      ClientRelationTeamOrderByColumn.AccountRole,
      $t('Account role'),
      $t('Owner first'),
      $t('Members first'),
    ),
  },
  TeamMemberEmailColumn: {
    fragment: TeamMemberEmailColumnFragmentDoc,
    component: markRaw(TeamMemberEmailColumn),
    columnDef: {
      title: $t('Email'),
      key: 'TeamMemberEmailColumn',
      renderUsing: {
        component: markRaw(TeamMemberEmailColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Email',
        extraction: (row: TeamMemberEmailColumnFragment) => memberUser(row).email ?? '',
      },
    ],
  },
  TeamMemberPhoneColumn: {
    fragment: TeamMemberPhoneColumnFragmentDoc,
    component: markRaw(TeamMemberPhoneColumn),
    columnDef: {
      title: $t('Phone'),
      key: 'TeamMemberPhoneColumn',
      renderUsing: {
        component: markRaw(TeamMemberPhoneColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Phone',
        extraction: (row: TeamMemberPhoneColumnFragment) => formatPhone(memberUser(row).phone ?? ''),
      },
    ],
  },
}

export default columns
